"use client";

import { useState, useRef, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Loader2, MessageSquare, Trash2, Send } from "lucide-react";
import { useToast } from '@/hooks/use-toast';
import NoteService from '@/api/note.service';
import { formatDistanceToNow } from 'date-fns';
import { Skeleton } from "@/components/ui/skeleton";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem } from "@/components/ui/command";

interface NotesTabProps {
  notes: any[];
  users: any[];
  entityType: 'customer' | 'deal';
  entityId: number;
  businessId?: number;
  isLoading: boolean;
  onNoteAdded: (note: any) => void;
  onNoteDeleted: (noteId: number) => void;
}

export function NotesTab({ notes, users, entityType, entityId, businessId, isLoading, onNoteAdded, onNoteDeleted }: NotesTabProps) {
  const [newNote, setNewNote] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState<number | null>(null);
  const [mentionQuery, setMentionQuery] = useState('');
  const [showMentions, setShowMentions] = useState(false);
  const [mentionPosition, setMentionPosition] = useState({ top: 0, left: 0 });
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!newNote.trim() || !businessId) return;
    
    setIsSaving(true);
    try {
      const data = {
        content: newNote,
        [entityType === 'customer' ? 'customerId' : 'dealId']: entityId,
      };
      
      const response = await NoteService.createNote(businessId, data);
      
      if (response.data.result === 'success') {
        toast({
          title: "Success",
          description: "Note added successfully",
        });
        onNoteAdded(response.data.note);
        setNewNote('');
      } else if (response.data.result === 'invalid') {
        window.location.href = '/logout';
      } else {
        toast({
          title: "Error",
          description: response.data.message || "Failed to add note",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Error adding note:", error);
      toast({
        title: "Error",
        description: "Failed to add note. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteNote = async (noteId: number) => {
    setIsDeleting(noteId);
    try {
      const response = await NoteService.deleteNote(noteId);
      
      if (response.data.result === 'success') {
        toast({
          title: "Success",
          description: "Note deleted successfully",
        });
        onNoteDeleted(noteId);
      } else if (response.data.result === 'invalid') {
        window.location.href = '/logout';
      } else {
        toast({
          title: "Error",
          description: response.data.message || "Failed to delete note",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Error deleting note:", error);
      toast({
        title: "Error",
        description: "Failed to delete note. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsDeleting(null);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    setNewNote(value);
    
    // Check for @ symbol
    const lastAtIndex = value.lastIndexOf('@');
    if (lastAtIndex !== -1 && lastAtIndex === value.length - 1) {
      setShowMentions(true);
      setMentionQuery('');
      
      // Position the mentions dropdown
      if (textareaRef.current) {
        const textAreaRect = textareaRef.current.getBoundingClientRect();
        setMentionPosition({
          top: textAreaRect.height,
          left: 0
        });
      }
    } else if (lastAtIndex !== -1 && value.charAt(lastAtIndex - 1) === ' ' || value.charAt(lastAtIndex - 1) === '\n' || lastAtIndex === 0) {
      const query = value.substring(lastAtIndex + 1).split(' ')[0];
      setMentionQuery(query);
      setShowMentions(true);
      
      // Position the mentions dropdown
      if (textareaRef.current) {
        const textAreaRect = textareaRef.current.getBoundingClientRect();
        setMentionPosition({
          top: textAreaRect.height,
          left: 0
        });
      }
    } else {
      setShowMentions(false);
    }
  };

  const handleSelectMention = (user: any) => {
    // Replace the @mention text with the selected user
    const lastAtIndex = newNote.lastIndexOf('@');
    if (lastAtIndex !== -1) {
      const beforeMention = newNote.substring(0, lastAtIndex);
      const afterMention = newNote.substring(lastAtIndex + mentionQuery.length + 1);
      setNewNote(`${beforeMention}@${user.firstName} ${afterMention}`);
    }
    setShowMentions(false);
  };

  const filteredUsers = users && users.filter(user => 
    user ? user.firstName?.toLowerCase().includes(mentionQuery.toLowerCase()) || 
    `${user.firstName} ${user.lastName}`.toLowerCase().includes(mentionQuery.toLowerCase()) : false
  ) || [];

  return (
    <div className="p-6 space-y-4">
      {/* Add Note Form */}
      <form onSubmit={handleSubmit} className="space-y-3">
        <div className="relative">
          <Textarea
            ref={textareaRef}
            placeholder="Add a note... Use @ to mention team members"
            value={newNote}
            onChange={handleInputChange}
            className="min-h-[100px] focus:ring-primary focus:border-primary resize-none"
            disabled={isSaving}
          />
          
          {showMentions && (
            <div 
              className="absolute bg-white border border-slate-200 rounded-md shadow-lg z-10 w-full max-h-[200px] overflow-y-auto"
              style={{ top: mentionPosition.top + 8, left: mentionPosition.left }}
            >
              <Command className="w-full">
                <CommandInput placeholder="Search users..." value={mentionQuery} onValueChange={setMentionQuery} />
                <CommandEmpty>No users found</CommandEmpty>
                <CommandGroup>
                  {filteredUsers && filteredUsers.map(user => (
                    <CommandItem 
                      key={user.id} 
                      value={user.firstName}
                      onSelect={() => handleSelectMention(user)}
                      className="flex items-center gap-2 p-2 cursor-pointer hover:bg-slate-100"
                    >
                      <Avatar className="h-6 w-6">
                        <AvatarFallback className="text-xs bg-primary/10 text-primary">
                          {user.firstName?.charAt(0)}{user.lastName?.charAt(0)}
                        </AvatarFallback>
                      </Avatar>
                      <span className="text-sm font-medium">{user.firstName} {user.lastName}</span>
                    </CommandItem>
                  ))}
                </CommandGroup>
              </Command>
            </div>
          )}
        </div>
        
        <div className="flex justify-end">
          <Button 
            type="submit" 
            disabled={isSaving || !newNote.trim()} 
            className="gap-2"
            size="sm"
          >
            {isSaving ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Send className="h-4 w-4" />
                Add Note
              </>
            )}
          </Button>
        </div>
      </form>

      {/* Notes List */}
      <div className="space-y-4 mt-6">
        <h3 className="text-sm font-medium text-slate-500">Notes & Comments</h3>
        
        {isLoading ? (
          <div className="space-y-6">
            {[1, 2, 3].map((i) => (
              <div key={i} className="flex gap-3">
                <Skeleton className="h-8 w-8 rounded-full" />
                <div className="space-y-2 flex-1">
                  <Skeleton className="h-4 w-1/4" />
                  <Skeleton className="h-16 w-full" />
                  <Skeleton className="h-3 w-1/5" />
                </div>
              </div>
            ))}
          </div>
        ) : notes.length === 0 ? (
          <div className="text-center py-10 border border-dashed rounded-md bg-slate-50 dark:bg-slate-900 border-slate-200 dark:border-slate-700">
            <MessageSquare className="h-10 w-10 text-slate-300 dark:text-slate-600 mx-auto mb-3" />
            <p className="text-sm text-slate-500 dark:text-slate-400">No notes yet</p>
            <p className="text-xs text-slate-400 dark:text-slate-500 mt-1">
              Add a note above to start collaborating
            </p>
          </div>
        ) : (
          <div className="space-y-6">
            {notes.map((note) => {
              const createdAt = new Date(note.createdAt);
              const timeAgo = formatDistanceToNow(createdAt, { addSuffix: true });
              
              // Process content to highlight mentions
              const contentWithMentions = note.content.replace(/@(\w+)/g, '<span class="text-primary font-medium">@$1</span>');
              
              return (
                <div key={note.id} className="relative group">
                  <div className="flex gap-3">
                    <Avatar className="h-8 w-8">
                      <AvatarFallback className="bg-primary/10 text-primary">
                        {note.user?.firstName?.charAt(0)}{note.user?.lastName?.charAt(0)}
                      </AvatarFallback>
                    </Avatar>
                    
                    <div className="flex-1">
                      <div className="flex justify-between items-start">
                        <p className="text-sm font-medium text-slate-700 dark:text-slate-300">
                          {note.user?.firstName} {note.user?.lastName}
                        </p>
                        
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
                          onClick={() => handleDeleteNote(note.id)}
                          disabled={isDeleting === note.id}
                        >
                          {isDeleting === note.id ? (
                            <Loader2 className="h-4 w-4 animate-spin text-red-500" />
                          ) : (
                            <Trash2 className="h-4 w-4 text-red-500" />
                          )}
                        </Button>
                      </div>
                      
                      <div 
                        className="text-sm text-slate-600 dark:text-slate-400 mt-1 break-words"
                        dangerouslySetInnerHTML={{ __html: contentWithMentions }}
                      />
                      
                      <p className="text-xs text-slate-400 dark:text-slate-500 mt-2">
                        {timeAgo}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
} 